import { useState, useCallback, useEffect } from 'react';

import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';

import { SidebarToggleIcon } from '../../assets/Icons/Icons';
import { useLeftPaneStyles } from './styles/left-pane.styles';
import { ActiveToolsMenuCard } from './ActiveToolsMenuCard';
import { ConversationHistory } from './ConversationHistory';
import type { ConversationRecord, Tool } from '../../types';
import { mcpChatTranslationRef } from '../../translation';

const noop = () => {};
const noopString = (_: string) => {};
const noopConversation = (_: ConversationRecord) => {};
const noopRename = (_id: string, _title: string) => {};

interface LeftPaneProps {
  collapsed: boolean;
  onToggle: () => void;
  onNewChat?: () => void;
  starredConversations?: ConversationRecord[];
  recentConversations?: ConversationRecord[];
  conversationsLoading?: boolean;
  conversationsError?: string;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchClear?: () => void;
  onSelectConversation?: (conversation: ConversationRecord) => void;
  onToggleStar?: (id: string) => void;
  onDeleteConversation?: (id: string) => void;
  onRenameConversation?: (id: string, title: string) => void;
  selectedConversationId?: string;
  availableTools?: Tool[];
  toolsLoading?: boolean;
}

export const LeftPane = ({
  collapsed,
  onToggle,
  onNewChat,
  starredConversations = [],
  recentConversations = [],
  conversationsLoading = false,
  conversationsError,
  searchQuery = '',
  onSearchChange,
  onSearchClear,
  onSelectConversation,
  onToggleStar,
  onDeleteConversation,
  onRenameConversation,
  selectedConversationId,
  availableTools = [],
  toolsLoading = false,
}: LeftPaneProps) => {
  const classes = useLeftPaneStyles();
  const { t } = useTranslationRef(mcpChatTranslationRef);
  const [autoFocusSearch, setAutoFocusSearch] = useState(false);

  const handleSearchClick = useCallback(() => {
    setAutoFocusSearch(true);
    onToggle();
  }, [onToggle]);

  // Reset autoFocusSearch when sidebar collapses
  useEffect(() => {
    if (collapsed) {
      setAutoFocusSearch(false);
    }
  }, [collapsed]);

  return (
    <Box
      className={`${classes.root} ${collapsed ? classes.collapsed : classes.expanded}`}
    >
      {collapsed ? (
        <Box className={classes.collapsedContent}>
          <IconButton
            size="small"
            aria-label={t('leftPane.ariaLabels.openSidebar')}
            onClick={onToggle}
          >
            <SidebarToggleIcon />
          </IconButton>
          <ActiveToolsMenuCard
            availableTools={availableTools}
            toolsLoading={toolsLoading}
          />
          <IconButton
            size="small"
            aria-label={t('leftPane.ariaLabels.addNewChat')}
            onClick={onNewChat}
          >
            <AddIcon fontSize="medium" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={t('leftPane.ariaLabels.search')}
            onClick={handleSearchClick}
          >
            <SearchIcon fontSize="medium" />
          </IconButton>
        </Box>
      ) : (
        <Box className={classes.sidebarWrapper}>
          <Box className={classes.sidebarInner}>
            <Box className={classes.sidebarHeader}>
              <Typography className={classes.sidebarHeaderText}>
                {t('leftPane.headerTitle')}
              </Typography>
              <Box className={classes.headerActions}>
                <ActiveToolsMenuCard
                  availableTools={availableTools}
                  toolsLoading={toolsLoading}
                  iconClassName={classes.headerIcon}
                />
                <IconButton
                  size="small"
                  aria-label={t('leftPane.ariaLabels.closeSidebar')}
                  onClick={onToggle}
                >
                  <SidebarToggleIcon className={classes.headerIcon} />
                </IconButton>
              </Box>
            </Box>
            <List disablePadding>
              <ListItemButton onClick={onNewChat}>
                <AddIcon fontSize="medium" className={classes.chatIcon} />
                <ListItemText
                  primary={t('leftPane.newChat')}
                  primaryTypographyProps={{ className: classes.newchat }}
                />
              </ListItemButton>
            </List>

            <Box className={classes.conversationHistory}>
              <ConversationHistory
                starredConversations={starredConversations}
                recentConversations={recentConversations}
                loading={conversationsLoading}
                error={conversationsError}
                searchQuery={searchQuery}
                onSearchChange={onSearchChange ?? noopString}
                onSearchClear={onSearchClear ?? noop}
                onSelectConversation={onSelectConversation ?? noopConversation}
                onToggleStar={onToggleStar ?? noopString}
                onDelete={onDeleteConversation ?? noopString}
                onRename={onRenameConversation ?? noopRename}
                selectedConversationId={selectedConversationId}
                focusSearch={autoFocusSearch}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
